<template>
  <div class="details-component">
    <div class="details-component__header">
      <h1>{{ $t('users.edit_user') }} </h1>
    </div>

    <div class="details-component__content content">
      <form class="form">
        <fieldset class="form__fieldset">
          <div class="form__field" :class="{'form__field_error' : errors.has('name')}">
            <label for="name" class="form__label">{{ $t('common.name') }}</label>
            <input type="text" class="form__input" name="name" id="name" v-model="form.name" v-validate="'required|max:255'" :data-vv-as="$t('common.name')" data-vv-delay="1000"  />
            <tooltip v-show="errors.has('name')" class="form__validation">{{ $t('errors.name_field') }}</tooltip>
          </div>
          <div class="form__field" :class="{'form__field_error' : errors.has('mobile')}">
            <label for="mobile" class="form__label">{{ $t('common.phone') }}</label>
            <input type="text" class="form__input" name="mobile" id="mobile" value="" v-model.trim="form.mobile" v-validate="'required|validMobile'" :data-vv-as="$t('common.phone')" data-vv-delay="1000"  />
            <tooltip v-show="errors.has('mobile')" class="form__validation">{{ $t('errors.phone_field') }}</tooltip>
          </div>
          <div class="form__field" :class="{'form__field_error' : errors.has('vehicleReg'), 'form__field_radiobuttons' : hasVehicles}">
          <label for="vehicleReg" class="form__label">{{ $tc('end_user.default_vehicle_reg', 'vehicleReg') }}</label>
            <div class="radiobuttons">
              <template v-for="vehicle in selectedUser.vehicles">
                <div class="radiobuttons__element" ref="vehicle">
                  <label>
                    <input type="radio" name="vehicle" v-model="selectedVehicle"
                      :id="vehicle"
                      :value="vehicle"
                      role="select vehicle" />
                    <span class="radiobuttons__control" ></span>
                    <span :for="vehicle" class="radiobuttons__description">{{ vehicle.vehicleReg }}</span>
                  </label>
                  <a class="link link--orange" @click="removeVehicle(vehicle)">{{ $t('common.delete') }}&nbsp;<i class="icon icon--cross"></i></a>
                </div>
              </template>
              <label>
                <input type="text" :placeholder="$t('add_permit.add_car')"
                  class="form__input add-vehicle"
                  name="vehicleReg" id="vehicleReg"
                  v-model.trim="form.vehicleReg"
                  ref="vehicleReg"
                  @keydown="stopInputAtDashAndSpace"
                  @keyup="trimVehicleReg()"/>
              </label>
          </div>
        </div>
        </fieldset>
        <div class="form__buttons buttonset">
          <router-link :to="{ name: 'dashboard'}" class="buttonset__button btn btn--secondary">
            {{ $t('common.discard') }}
          </router-link>
          <button type="submit" class="buttonset__button btn btn--main" @click.prevent.stop="onSubmit()">
          {{ $t('common.save') }}</button>
        </div>
      </form>
    </div>
    <WarningModal v-if="showWarningModal" @close="closeWarningModal">
      <h1 slot="header">{{ warningHeader }}</h1>
      <p slot="body">{{ warningBody }}</p>
    </WarningModal>

  </div>
</template>

<script>
import { parse, format, isValidNumber } from 'libphonenumber-js';
import { mapGetters } from 'vuex';
import * as mTypes from 'store/mutation-types';
import Tooltip from 'common/Tooltip';
import WarningModal from 'common/WarningModal';
import { sanitizeVehicleRegNumber } from '../../utils';

export default {
  components: {
    Tooltip,
    WarningModal,
  },
  data() {
    return {
      form: {},
      warningHeader: null,
      warningBody: null,
      showWarningModal: false,
      selectedVehicle: null,
      removedVehicles: [],
    };
  },
  mounted() {
    this.fetchUser();
  },
  created() {
    this.$store.commit(`users/user/${mTypes.CLEAR_STATE}`);
    this.resetValidators();
    this.createValidators();
  },
  computed: mapGetters({
    user: 'user/user/user',
    selectedUser: 'users/user/user',
    vehicleError: 'users/vehicles/error',
  }),
  methods: {
    trimVehicleReg() {
      if (this.form.vehicleReg) {
        this.form.vehicleReg = sanitizeVehicleRegNumber(this.form.vehicleReg);
      }
    },
    stopInputAtDashAndSpace(event) {
      if (event.key === '-' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    fetchUser() {
      this.$store.dispatch('users/user/fetch', { id: this.user.data.selectedRole.id, user: this.user }).then(() => {
        this.form = this.selectedUser;
        this.formatMobileNrForForm();
        this.selectDefaultVehicle();
      });
    },
    formatMobileNrForForm() {
      if (this.form.mobile) {
        this.form.mobile = format({ country: this.form.mobileCountry, phone: this.form.mobile.toString() }, 'International_plaintext');
      } else {
        this.form.mobile = '';
      }
    },
    onSubmit() {
      this.$validator.validateAll(
        { name: this.form.name,
          mobile: this.form.mobile,
        })
        .then((success) => {
          this.$store.commit(`users/user/${mTypes.DATA_ERROR}`, null);
          if (success) {
            this.parsePhoneBeforeSave();
            this.updateVehicleDefaults();
            this.$store.dispatch('users/user/saveUser', { selectedUser: this.form, user: this.user })
              .then(() => {
                if (this.userError) {
                  this.warningHeader = this.$t('common.warning');
                  this.warningBody = this.$t('users.save_error_email');
                  this.showWarningModal = true;
                } else {
                  this.$router.push({ name: 'dashboard' });
                }
              })
              .catch(() => {
                this.$store.commit(`users/user/${mTypes.DATA_ERROR}`, true);
              });
          } else {
            this.$store.commit(`users/user/${mTypes.DATA_ERROR}`, true);
          }
        })
        .catch(() => {
          this.$store.commit(`users/user/${mTypes.DATA_ERROR}`, true);
        });
    },
    parsePhoneBeforeSave() {
      const parsedPhone = parse(this.form.mobile, { country: { default: 'EE' } });
      this.form.mobileCountry = parsedPhone.country;
      this.form.mobile = parsedPhone.phone;
    },
    resetValidators() {
      this.$validator.remove('validMobile');
      this.$validator.clean();
    },
    closeWarningModal() {
      this.showWarningModal = false;
    },
    createValidators() {
      this.createPhoneNrValidator();
    },
    createPhoneNrValidator() {
      this.$validator.extend('validMobile', {
        getMessage: () => 'Please insert valid phone number. Mobile number must include country code.',
        validate: (value) => {
          if (value.trim === '') {
            return true;
          }
          const parsed = parse(value, { country: { default: 'EE' } });
          return isValidNumber(parsed);
        },
      });
    },
    hasVehicles() {
      return this.selectedUser.vehicles && this.selectedUser.vehicles.length > 0;
    },
    selectDefaultVehicle() {
      if (this.selectedUser.vehicles) {
        this.selectedVehicle = this.selectedUser.vehicles.find(el => el.default);
      }
    },
    removeVehicle(vehicle) {
      this.$store.dispatch('users/vehicles/delete', { id: vehicle.id, user: this.user })
        .then(() => {
          if (!this.vehicleError) {
            const index = this.selectedUser.vehicles.findIndex(obj => obj === vehicle);
            this.$delete(this.selectedUser.vehicles, index);
          } else {
            this.showWarningModal = true;
            this.warningHeader = this.$t('common.warning');
            this.warningBody = this.$t(`errors.${this.vehicleError.body.error}`);
          }
        });
      this.$store.commit(`users/vehicles/${mTypes.DATA_ERROR}`, null);
    },
    updateVehicleDefaults() {
      if (this.selectedUser.vehicles) {
        this.selectedUser.vehicles.forEach((o, i, a) => {
          a[i].default = a[i].vehicleReg === this.selectedVehicle.vehicleReg;
        });
        this.form.vehicles = this.selectedUser.vehicles;
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/end-user/EditProfile.vue